import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import {
  useInfiniteQuery,
  useQuery,
  type UseQueryResult,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { API, IAPI } from "../APIService";
import type { AxiosResponse } from "axios";
import { buildQueryKey } from "@/utils/query";

export interface IModifierSet {
  alt_name: string | null;
  number_of_items: number;
  name: string;
  id: number;
  internal_name: string | null;
  updated_at: string;
  description: string | null;
  selection_required: boolean;
  default_order: number;
  number_of_modifiers: number;
  active: boolean;
  category_type: number;
  created_at: string;
}

export interface IModifierSetsListParams {
  exclude_menu_items?: Array<number>;
  exclude_modifiers?: Array<number>;
  page?: number;
  page_size?: number;
  search?: string;
  order_by?: string;
  active?: boolean;
}

export interface ICreateModifierSetParams {
  name: string;
  selection_required?: boolean;
  active?: boolean;
  category_type: number;
  description?: string | null;
  internal_name?: string | null;
  default_order?: number;
}

export interface IBulkCreateModifierSetRelationshipsParams {
  modifier_category_id?: ID;
  menu_item_menu_categories: Array<{
    menu_item_id: ID;
    menu_category_id: ID;
  }>;
  modifier_category_menu_items: Array<{
    modifier_category_id: ID;
    menu_item_id: ID;
  }>;
  modifier_modifier_categories: Array<{
    modifier_id: ID;
    modifier_category_id: ID;
  }>;
}

export interface IBulkCreateModifierSetRelationshipsResponse {
  modifier_modifier_categories: Array<{
    cost_price: number | null;
    id: number;
    is_default_option: boolean | null;
    order: number | null;
    modifier_id: number;
    value: string | null;
    modifier_category_id: number;
  }>;

  modifier_category_menu_items: Array<{
    selection_required: boolean | null;
    id: number;
    order: number | null;
    modifier_category_id: number;
    menu_item_id: number;
  }>;

  menu_item_menu_categories: Array<{
    cost_price: number | null;
    id: number;
    promotion_value: number | null;
    order: number | null;
    value: string | null;
    menu_item_id: number;
    is_promoted: boolean | null;
    menu_category_id: number;
  }>;
}

export interface IModifierSetsListResponse
  extends APIPaginatedResponse<IModifierSet> {}

export interface IModifierSetsReponseAll {
  pages: IModifierSetsListResponse[];
  pageParams: number[];
}

export interface IModifierSetItemsRelationshipsParams {
  modifier_category_id: ID;
}

export interface IModifierSetItemsRelationship {
  modifier_category_id: number;
  menu_item: {
    active: boolean;
    number_of_modifier_categories: number;
    promotion_value: number;
    order: number;
    in_stock: boolean;
    display_image: string;
    stock_level: number;
    allow_custom_amount: boolean;
    name: string;
    is_promoted: boolean;
    display_description: string;
    value: number;
    number_of_categories: number;
    id: number;
    item_type: number;
  };
  menu_item_id: number;
  order: number;
  selection_required: boolean;
  modifier_category: {
    active: boolean;
    internal_name: string;
    order: number;
    selection_required: boolean;
    number_of_modifiers: number;
    name: string;
    category_type: number;
    id: number;
  };
  id: number;
}

export interface IModifierSetItemsRelationshipsResponse
  extends APIPaginatedResponse<IModifierSetItemsRelationship> {}

export interface IModifierSetModifiersRelationship {
  value: number;
  id: number;
  modifier_id: number;
  modifier_category_id: number;
  order: number;
  is_default_option: boolean;
  cost_price: number;
  modifier: {
    name: string;
    description: string;
    value: number;
    in_stock: boolean;
    id: number;
    order: number;
    is_default_option: boolean;
    cost_price: number;
    active: boolean;
    internal_name: string;
  };
  modifier_category: {
    name: string;
    id: number;
    selection_required: boolean;
    order: number;
    category_type: number;
    number_of_modifiers: number;
    active: boolean;
    internal_name: string;
  };
}

export interface IModifierSetModifiersRelationshipResponse
  extends APIPaginatedResponse<IModifierSetModifiersRelationship> {}

export interface IModifierSetModifiersRelationshipsParams {
  category_id: ID;
}

export interface IUpdateModifierSetParams extends ICreateModifierSetParams {
  id: ID;
}

export interface IDeleteModifierSetParams {
  id: ID;
}

export interface IDeleteItemModifierSetRelationshipParamsBase {
  relationship_id: ID;
  modifier_category_id: ID;
}

export interface IDeleteModifierModifierSetRelationshipParamsBase {
  relationship_id: ID;
  modifier_category_id: ID;
}

export interface IModifierSetsService {
  // List
  listAll: (
    params: IModifierSetsListParams,
  ) => UseInfiniteQueryResult<IModifierSetsReponseAll, Error>;
  list: (
    params: IModifierSetsListParams,
  ) => UseQueryResult<IModifierSetsListResponse, Error>;

  // CRUD
  create: () => UseMutationResult<
    IModifierSet,
    Error,
    ICreateModifierSetParams
  >;
  retrieve: (modifierSetID: ID) => UseQueryResult<IModifierSet, Error>;
  update: () => UseMutationResult<
    IModifierSet,
    Error,
    IUpdateModifierSetParams
  >;
  delete: () => UseMutationResult<boolean, Error, IDeleteModifierSetParams>;

  // Relationships
  bulkCreateRelationships: () => UseMutationResult<
    IBulkCreateModifierSetRelationshipsResponse,
    Error,
    IBulkCreateModifierSetRelationshipsParams
  >;

  // List Relationships
  listItemsRelationships: (
    params: IModifierSetItemsRelationshipsParams,
  ) => UseQueryResult<Array<IModifierSetItemsRelationship>, Error>;

  listModifiersRelationships: (
    params: IModifierSetModifiersRelationshipsParams,
  ) => UseQueryResult<Array<IModifierSetModifiersRelationship>, Error>;

  // DeleteRelationships Mutations
  deleteItemModifierSetRelationships: () => UseMutationResult<
    boolean,
    Error,
    IDeleteItemModifierSetRelationshipParamsBase
  >;
  deleteModifierModifierSetRelationships: () => UseMutationResult<
    boolean,
    Error,
    IDeleteModifierModifierSetRelationshipParamsBase
  >;
}

export class ModifierSetsService implements IModifierSetsService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  // List

  listAll(
    params: IModifierSetsListParams,
  ): UseInfiniteQueryResult<IModifierSetsReponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null),
    );
    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["organisation", "modifier-sets", "all"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<
          AxiosResponse<IModifierSetsListResponse>
        >("/menu/modifier_categories", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: { ...updatedParams, page: pageParam, page_size: 100 },
        });

        return data;
      },
      getNextPageParam: (lastPage: IModifierSetsListResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }

  list(
    params: IModifierSetsListParams,
  ): UseQueryResult<IModifierSetsListResponse, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== null && value !== undefined,
      ),
    );

    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "modifier_sets"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IModifierSetsListResponse>
        >("/menu/modifier_categories", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: updatedParams,
        });

        return {
          results: data.results,
          count: data.count,
          next: data.next,
          previous: data.previous,
        };
      },
    });
  }

  // CRUD

  create(): UseMutationResult<IModifierSet, Error, ICreateModifierSetParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "modifier_sets", "create"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<IModifierSet>>(
          "/menu/me/modifier_categories/",
          {
            endpointService: EndpointService.Core,
            venueSpecific: false,
            params,
          },
        );
        return data;
      },
      onMutate: async (newModifierSet) => {},
      onError: (err, newModifierSet, context) => {},
      onSettled: () => {},
    });
  }

  retrieve(modifierSetID: ID): UseQueryResult<IModifierSet, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "modifier_sets", "retrieve", modifierSetID],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IModifierSet>>(
          `/menu/modifier_categories/${modifierSetID}`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
    });
  }

  update(): UseMutationResult<IModifierSet, Error, IUpdateModifierSetParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "modifier_sets", "update"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...updatedParams } = params;
        const { data } = await this.api.patch<AxiosResponse<IModifierSet>>(
          `/menu/me/modifier_categories/${id}`,
          {
            endpointService: EndpointService.Core,
            venueSpecific: false,
            params: updatedParams,
          },
        );
        return data;
      },
      onMutate: async (newModifierSet) => {},
      onError: (err, newModifierSet, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["operations", "modifier_sets", "retrieve", variables.id],
        });
      },
    });
  }

  delete(): UseMutationResult<boolean, Error, IDeleteModifierSetParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "modifier_sets", "delete"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete<AxiosResponse<boolean>>(
          `/menu/me/modifier_categories/${params.id}`,
        );
        return true;
      },
      onMutate: async (newDeleteModifierSetParams) => {},
      onError: (err, newDeleteModifierSetParams, context) => {
        return false;
      },
      onSettled: () => {},
    });
  }

  // Relationships

  bulkCreateRelationships(): UseMutationResult<
    IBulkCreateModifierSetRelationshipsResponse,
    Error,
    IBulkCreateModifierSetRelationshipsParams
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "modifier_sets", "bulkCreateRelationships"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { modifier_category_id, ...restParams } = params;
        const { data } = await this.api.post<
          AxiosResponse<IBulkCreateModifierSetRelationshipsResponse>
        >("/menu/me/relationships/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: restParams,
        });
        return data;
      },
      onMutate: async (newModifierSetRelationships) => {},
      onError: (err, newModifierSetRelationships, context) => {},
      onSettled: (data, error, variables) => {
        if (variables.modifier_category_id) {
          queryClient.invalidateQueries({
            queryKey: ["operations", "modifier_sets", "itemsRelationships"],
          });
          queryClient.invalidateQueries({
            queryKey: ["operations", "modifier_sets", "modifiersRelationships"],
          });
        }
      },
    });
  }

  // List Relationships

  listItemsRelationships(
    params: IModifierSetItemsRelationshipsParams,
  ): UseQueryResult<Array<IModifierSetItemsRelationship>, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "modifier_sets", "itemsRelationships"],
        venueID: this.api.selectedVenue,
        params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IModifierSetItemsRelationshipsResponse>
        >("/menu/me/modifiercategory_menuitems/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: {
            ...params,
            page: 1,
            page_size: 50,
          },
        });

        return data.results;
      },
    });
  }

  listModifiersRelationships(
    params: IModifierSetModifiersRelationshipsParams,
  ): UseQueryResult<Array<IModifierSetModifiersRelationship>, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "modifier_sets", "modifiersRelationships"],
        venueID: this.api.selectedVenue,
        params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IModifierSetModifiersRelationshipResponse>
        >("/menu/me/modifier_modifiercategories/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: {
            ...params,
            page: 1,
            page_size: 50,
          },
        });

        return data.results;
      },
    });
  }

  // Delete Relationships

  deleteItemModifierSetRelationships(): UseMutationResult<
    boolean,
    Error,
    IDeleteItemModifierSetRelationshipParamsBase
  > {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: [
          "operations",
          "modifier_sets",
          "deleteItemModifierSetRelationships",
        ],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete<AxiosResponse<boolean>>(
          `/menu/me/modifiercategory_menuitems/${params.relationship_id}`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return true;
      },
      onMutate: async (newDeleteRelationshipParams) => {},
      onError: (err, newDeleteRelationshipParams, context) => {
        return false;
      },
      onSettled: (data, error, variables) => {
        if (variables.modifier_category_id) {
          queryClient.invalidateQueries({
            queryKey: ["operations", "modifier_sets", "itemsRelationships"],
          });
        }
      },
    });
  }

  deleteModifierModifierSetRelationships(): UseMutationResult<
    boolean,
    Error,
    IDeleteModifierModifierSetRelationshipParamsBase
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: [
          "operations",
          "modifier_sets",
          "deleteModifierModifierSetRelationships",
        ],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete<AxiosResponse<boolean>>(
          `/menu/me/modifier_modifiercategories/${params.relationship_id}`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return true;
      },
      onMutate: async (newDeleteRelationshipParams) => {},
      onError: (err, newDeleteRelationshipParams, context) => {
        return false;
      },
      onSettled: (data, error, variables) => {
        if (variables.modifier_category_id) {
          queryClient.invalidateQueries({
            queryKey: ["operations", "modifier_sets", "modifiersRelationships"],
          });
        }
      },
    });
  }
}
